import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { dispatch } from '@ngneat/effects';
import { TruncateStringPipe } from '@shared/core-utils';
import { switchAccount } from '@tmx/auth';
import { subscribeOnce } from '@tmx/core';
import { ToastService } from '@tmx/modus-operandi';
import { NavDropdownComponent } from '@tmx/operandi';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

import { DropdownOption } from '../../models/dropdown-option';
import { userAuthenticated$ } from '../../repositories/account-manager/account-manager.repository';
import { BrazeEventsService } from '../../services/braze-events.service';
import { loadPartnerLevelAccounts } from './repositories/switch-accounts.actions';
import {
  deregisterSwitchAccountsEffects,
  registerSwitchAccountsEffects,
} from './repositories/switch-accounts.effects';
import {
  customerAccounts$,
  partnerAccounts$,
  resetSwitchAccountsStore,
} from './repositories/switch-accounts.repository';

@Component({
  selector: 'switch-accounts',
  templateUrl: 'switch-accounts.component.html',
  styleUrls: ['./switch-accounts.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoModule,
    NavDropdownComponent,
    TruncateStringPipe,
    ReactiveFormsModule,
  ],
})
export class SwitchAccountsComponent implements OnDestroy, OnInit {
  private readonly router = inject(Router);
  private readonly toastService = inject(ToastService);
  private readonly translocoService = inject(TranslocoService);
  private readonly brazeEventsService = inject(BrazeEventsService);

  loading = toSignal(
    combineLatest([
      partnerAccounts$.pipe(
        map(({ fetchStatus }) => fetchStatus === 'fetching')
      ),
      customerAccounts$.pipe(
        map(({ fetchStatus }) => fetchStatus === 'fetching')
      ),
    ]).pipe(map((statuses) => statuses.some((status) => status))),
    { initialValue: false }
  );

  searchControl = new FormControl('');
  private readonly search = toSignal(
    this.searchControl.valueChanges.pipe(map((value) => (value ?? '').trim())),
    {
      initialValue: '',
    }
  );

  partnerAccountsSource = toSignal(
    partnerAccounts$.pipe(map(({ data }) => data)),
    {
      initialValue: [],
    }
  );
  partnerAccounts = computed(() => {
    return this.filterAccounts(this.partnerAccountsSource());
  });

  customerAccountsSource = toSignal(
    customerAccounts$.pipe(map(({ data }) => data)),
    {
      initialValue: [],
    }
  );
  customerAccounts = computed(() => {
    return this.filterAccounts(this.customerAccountsSource());
  });

  ngOnInit() {
    registerSwitchAccountsEffects();

    dispatch(loadPartnerLevelAccounts());
  }

  switch(alias: string) {
    if (alias === '') {
      return;
    }

    switchAccount(alias);

    subscribeOnce(userAuthenticated$, {
      next: () => {
        this.toastService.add({
          type: 'success',
          content: this.translocoService.translate(
            'components.switchAccounts.switchedIntoAccount'
          ),
        });

        this.router.navigateByUrl('/ams');

        this.brazeEventsService.logBrazeCustomEvents(
          this.brazeEventsService.SWITCHED_ACCOUNT
        );
      },
    });
  }

  ngOnDestroy() {
    resetSwitchAccountsStore();
    deregisterSwitchAccountsEffects();
  }

  private filterAccounts(accounts: DropdownOption[]) {
    if (this.search() === '') {
      return accounts;
    }

    const query = new RegExp(this.search(), 'i');
    return accounts.filter((account) => query.test(account.displayName));
  }
}
